import type { GetOrderQuery } from '@codezo/graphql';

type Order = NonNullable<GetOrderQuery['getOrder']>;
type OrderItem = Order['items'][number];

/**
 * What is left of the page's own arithmetic: nothing.
 *
 * This file held six computations standing in for fields that didn't exist —
 * margin percentage, the order total, money collected, total quantity, line
 * total, and the awaiting-confirmation count. All six now come off the schema,
 * and all six are gone. Read them straight:
 *
 *   order.summary.marginPercentage      order.summary.grossAmount
 *   order.paymentSummary.totalPaidAmount     order.totalQuantity
 *   item.lineTotal                      order.itemsAwaitingConfirmation
 *
 * Derived values belong on the server: two apps render the same order, and a
 * rule computed in one drifts from the other and from the backend's own answer.
 * If something is needed and no field carries it, that is a backend ask — not a
 * function here.
 */

/**
 * Items in the order the studio arranged them.
 *
 * Not a derivation — it honours `sortOrder`, which the server already sends and
 * which nothing was reading, in v1 or v2. Both pages were rendering items in
 * whatever order the API happened to return them.
 */
export function inSortOrder(items: OrderItem[]): OrderItem[] {
	return [...items].sort((a, b) => a.sortOrder - b.sortOrder);
}
